import type { Store } from "#puf/store/index.ts";
import { setup } from "xstate";

/**
 * Events the playback machine consumes.
 *
 * - `TOGGLE` — flip play/pause. UI button binds to this.
 * - `PLAY` / `PAUSE` — explicit edges, used by programmatic callers.
 * - `FPS_CHANGED` — fired when fps changes so the running `after` re-enters
 *   and rebuilds with the new delay. Without it, an in-flight tick keeps the
 *   old tempo until the next transition.
 */
type PlaybackEvent =
  | { type: "TOGGLE" }
  | { type: "PLAY" }
  | { type: "PAUSE" }
  | { type: "FPS_CHANGED" };

/**
 * Input passed at actor creation time. The store is a stable singleton.
 * `getMachineActor` returns the current user-machine actor — it's a getter
 * because the user-machine actor is rebuilt whenever the user's `stateMachine`
 * config changes, but `useActorRef` captures input at mount time. The host
 * keeps the current actor in a ref and exposes it via this getter so each
 * tick reads the live actor. Returns `null` when no machine is mounted yet.
 */
interface PlaybackInput {
  store: Store;
  getMachineActor: () => PlaybackMachineActor | null;
}

type OptionalPlaybackInput = PlaybackInput | undefined;

interface PlaybackContext {
  store: Store | null;
  getMachineActor: (() => PlaybackMachineActor | null) | null;
}

export interface PlaybackMachineActor {
  getSnapshot: () => { value: unknown };
  send: (event: { type: "RANGE_END" }) => void;
}

/**
 * Pure play/pause + tick state graph. Owns timing only — frame/state-machine
 * coupling lives in the `advance` action which reads from `context.store` and
 * `context.machineActor`. The `after: { tick }` delay is dynamic: it reads the
 * live `fps` from the store so changing fps mid-playback adjusts the cadence
 * after the next state re-entry (which `FPS_CHANGED` triggers).
 *
 * On range overflow, `advance` raises `RANGE_END` on the user's machine actor;
 * if a transition catches it, the snapshot value flips and we jump to the new
 * state's range start; otherwise the snapshot value is unchanged and we restart
 * at the current range's start (loop-within-range fallback).
 */
export const playbackMachine = setup({
  types: {
    events: {} as PlaybackEvent,
    input: {} as OptionalPlaybackInput,
    context: {} as PlaybackContext,
  },
  delays: {
    tick: ({ context }) => {
      const fps = context.store?.getSnapshot().context.fps ?? 60;
      return Math.max(16, 1000 / Math.max(1, fps));
    },
  },
  actions: {
    advance: ({ context }) => {
      const store = context.store;
      const machineActor = context.getMachineActor?.();
      const snapshot = store?.getSnapshot();
      if (!machineActor || !snapshot || !store) return;
      const sctx = snapshot.context;
      const stateId = String(machineActor.getSnapshot().value);
      const range = sctx.stateFrames[stateId];
      const cur = sctx.activeFrame;
      const frameCount = sctx.frames.length;

      if (!range) {
        // No range mapped for the current state — fall back to linear advance
        // wrapping at the frame-count boundary.
        if (frameCount <= 0) return;
        store.trigger["activeFrame.set"]({ index: (cur + 1) % frameCount });
        return;
      }

      const [start, end] = range;
      if (cur < start || cur > end) {
        store.trigger["activeFrame.set"]({ index: start });
        return;
      }
      if (cur >= end) {
        // Range complete — the user's machine MAY catch RANGE_END and flip
        // state; if not, snapshot stays put and we loop within this range.
        machineActor.send({ type: "RANGE_END" });
        const newId = String(machineActor.getSnapshot().value);
        const newRange = sctx.stateFrames[newId];
        store.trigger["activeFrame.set"]({ index: newRange?.[0] ?? start });
        return;
      }
      store.trigger["activeFrame.set"]({ index: cur + 1 });
    },
  },
}).createMachine({
  id: "playback",
  initial: "idle",
  context: ({ input }) => ({
    store: input?.store ?? null,
    getMachineActor: input?.getMachineActor ?? null,
  }),
  states: {
    idle: {
      on: {
        TOGGLE: "playing",
        PLAY: "playing",
      },
    },
    playing: {
      after: {
        tick: { target: "playing", actions: "advance", reenter: true },
      },
      on: {
        TOGGLE: "idle",
        PAUSE: "idle",
        FPS_CHANGED: { target: "playing", reenter: true },
      },
    },
  },
});
